import React from 'react';
import { DARK_THEME_CSS } from './darkTheme';

// ─────────────────────────────────────────────────────────────
// RIFITEL · COST & QUANTITY CONTROL — DESIGN SYSTEM  "TIDE"
//
// Ocean cyan-blue identity. Light is the default, balanced canvas;
// dark is a deep ocean-navy companion. A deep-sea gradient rail
// carries the brand; green/amber/red stay reserved for budget
// meaning (under / committed / over). Every original CSS-variable
// NAME is preserved so all 24 pages inherit this skin untouched.
// ─────────────────────────────────────────────────────────────

export const GlobalStyles = () => (
  <>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
    <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Space+Grotesk:wght@400;500;600;700&display=swap" />
    <style>{`
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    /* ════════════════════════════════════════════
       LIGHT  (default workspace — the hero)
       ════════════════════════════════════════════ */
    :root {
      --bg-base:      #f1f5f8;
      --bg-surface:   #ffffff;
      --bg-sidebar:   #0a3040;     /* fallback; rail paints a gradient */
      --topbar-bg:    #ffffff;
      --bg-card:      #ffffff;
      --bg-elevated:  #ffffff;
      --bg-hover:     #e9f1f5;
      --border:       #e2eaef;
      --border-light: #d0dce3;
      --border-strong: #94a3b8;   /* inputs that must read as editable */

      /* rail (always light text over the deep-ocean gradient) */
      --sidebar-text-primary:   #ffffff;
      --sidebar-text-secondary: rgba(255,255,255,0.68);
      --sidebar-text-muted:     rgba(255,255,255,0.42);
      --sidebar-border:         rgba(255,255,255,0.10);
      --sidebar-bg-hover:       rgba(255,255,255,0.09);

      /* brand / accent — ocean cyan-blue */
      --brand:        #0d7fb5;
      --brand-2:      #22d3ee;
      --accent:       #0b318bff;
      --accent-dim:   #081e53ff;
      --accent-hover: #374a78ff;

      /* semantic (budget meaning) */
      --green:        #16a34a;
      --green-dim:    rgba(22,163,74,0.11);
      --amber:        #e08a00;
      --amber-dim:    rgba(224,138,0,0.12);
      --red:          #f0395b;
      --red-dim:      rgba(240,57,91,0.10);
      --purple:       #6366f1;
      --purple-dim:   rgba(99,102,241,0.11);
      --cyan:         #0fb0c9;
      --cyan-dim:     rgba(15,176,201,0.11);
      --blue:         #2563eb;
      --blue-dim:     rgba(37,99,235,0.10);
      --indigo:       #6366f1;
      --indigo-dim:   rgba(99,102,241,0.10);
      --gray:         #64748b;
      --gray-dim:     rgba(13,27,36,0.05);

      --text-primary:   #0d1b24;
      --text-secondary: #51626c;
      --text-muted:     #8a9aa3;

      --font:  'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      --mono:  'Space Grotesk', 'SF Mono', ui-monospace, monospace;

      --shadow-sm: 0 1px 2px rgba(13,27,36,0.06), 0 1px 3px rgba(13,27,36,0.04);
      --shadow:    0 6px 20px rgba(13,27,36,0.08), 0 14px 40px rgba(13,127,181,0.08);
      --shadow-lg: 0 30px 70px rgba(13,27,36,0.18);
      --ring:      0 0 0 3px rgba(13,127,181,0.22);

      --radius:    12px;
      --radius-lg: 18px;

      --rail: #08303f;
      --dot:  rgba(13,27,36,0.045);
    }

    /* ════════════════════════════════════════════
       DARK  (deep ocean-navy companion)
       ════════════════════════════════════════════ */
    [data-theme="dark"] {
      --bg-base:      #0a141c;
      --bg-surface:   #0e1a23;
      --bg-sidebar:   #08222e;
      --topbar-bg:    #0e1a23;
      --bg-card:      #111f2a;
      --bg-elevated:  #172935;
      --bg-hover:     #1c3140;
      --border:       #213342;
      --border-light: #2d4456;
      --border-strong: #4d6d85;   /* inputs that must read as editable */

      --sidebar-text-primary:   #ffffff;
      --sidebar-text-secondary: rgba(255,255,255,0.64);
      --sidebar-text-muted:     rgba(255,255,255,0.40);
      --sidebar-border:         rgba(255,255,255,0.08);
      --sidebar-bg-hover:       rgba(255,255,255,0.08);

      --brand:        #25b9dd;
      --brand-2:      #3ce0e6;
      --accent:       #0b318bff;
      --accent-dim:   rgba(37,185,221,0.16);
      --accent-hover: #4ccced;

      --green:        #34d399;
      --green-dim:    rgba(52,211,153,0.14);
      --amber:        #fbbf24;
      --amber-dim:    rgba(251,191,36,0.15);
      --red:          #fb6f8c;
      --red-dim:      rgba(251,111,140,0.15);
      --purple:       #a5b4fc;
      --purple-dim:   rgba(165,180,252,0.15);
      --cyan:         #38d6ee;
      --cyan-dim:     rgba(56,214,238,0.14);
      --blue:         #60a5fa;
      --blue-dim:     rgba(96,165,250,0.15);
      --indigo:       #a5b4fc;
      --indigo-dim:   rgba(165,180,252,0.15);
      --gray:         #8ba0ad;
      --gray-dim:     rgba(255,255,255,0.06);

      --text-primary:   #e7f1f6;
      --text-secondary: #9eb1bd;
      --text-muted:     #68808f;

      --shadow-sm: 0 1px 2px rgba(0,0,0,0.4);
      --shadow:    0 14px 40px rgba(0,0,0,0.5);
      --shadow-lg: 0 30px 70px rgba(0,0,0,0.62);
      --ring:      0 0 0 3px rgba(37,185,221,0.34);

      --rail: #072734;
      --dot:  rgba(255,255,255,0.03);
    }

    /* explicit light block mirrors :root (in case attribute is set) */
    [data-theme="light"] {
      --bg-base:#f1f5f8; --bg-surface:#ffffff; --topbar-bg:#ffffff;
      --bg-card:#ffffff; --bg-elevated:#ffffff; --bg-hover:#e9f1f5;
      --border:#e2eaef; --border-light:#d0dce3; --border-strong:#94a3b8;
      --accent:#0b318bff; --accent-dim:rgba(13,127,181,0.10); --accent-hover:#0a688f;
      --brand:#0d7fb5; --brand-2:#22d3ee;
      --green:#16a34a; --green-dim:rgba(22,163,74,0.11);
      --amber:#e08a00; --amber-dim:rgba(224,138,0,0.12);
      --red:#f0395b; --red-dim:rgba(240,57,91,0.10);
      --purple:#6366f1; --purple-dim:rgba(99,102,241,0.11);
      --cyan:#0fb0c9; --cyan-dim:rgba(15,176,201,0.11);
      --blue:#2563eb; --blue-dim:rgba(37,99,235,0.10);
      --indigo:#6366f1; --indigo-dim:rgba(99,102,241,0.10);
      --gray:#64748b; --gray-dim:rgba(13,27,36,0.05);
      --text-primary:#0d1b24; --text-secondary:#51626c; --text-muted:#8a9aa3;
      --shadow-sm:0 1px 2px rgba(13,27,36,0.06),0 1px 3px rgba(13,27,36,0.04);
      --shadow:0 6px 20px rgba(13,27,36,0.08),0 14px 40px rgba(13,127,181,0.08);
      --shadow-lg:0 30px 70px rgba(13,27,36,0.18);
      --ring:0 0 0 3px rgba(13,127,181,0.22);
      --rail: #08303f;
      --dot: rgba(13,27,36,0.045);
    }

    html { -webkit-text-size-adjust: 100%; }

    body {
      font-family: var(--font);
      background: var(--bg-base);
      color: var(--text-primary);
      font-size: 14px;
      line-height: 1.6;
      letter-spacing: -0.006em;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    /* soft engineering dot-grid whisper — ties to the QS world */
    body::before {
      content: '';
      position: fixed; inset: 0;
      background-image: radial-gradient(var(--dot) 1px, transparent 1px);
      background-size: 22px 22px;
      pointer-events: none;
      z-index: -1;
    }

    h1,h2,h3,h4 { letter-spacing: -0.03em; font-weight: 800; }

    button { font-family: var(--font); }
    input, select, textarea { font-family: var(--font); color: var(--text-primary); outline: none; }
    input::placeholder, textarea::placeholder { color: var(--text-muted); }
    select option { background: var(--bg-elevated); color: var(--text-primary); }

    [style*="--mono"], code, kbd {
      font-variant-numeric: tabular-nums;
    }

    a { color: var(--accent); text-decoration: none; }
    ::selection { background: rgba(13,127,181,0.26); color: #fff; }

    :focus-visible { outline: none; box-shadow: var(--ring); border-radius: 9px; }

    ::-webkit-scrollbar { width: 10px; height: 10px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb {
      background: var(--border-light); border-radius: 999px;
      border: 2px solid transparent; background-clip: padding-box;
    }
    ::-webkit-scrollbar-thumb:hover { background: var(--text-muted); background-clip: padding-box; }
    * { scrollbar-width: thin; scrollbar-color: var(--border-light) transparent; }

    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    @keyframes spin { 100% { transform: rotate(360deg); } }
    @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
    @keyframes zoomIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
    .fade-in { animation: fadeIn 0.36s cubic-bezier(0.22,1,0.36,1); }
    .zoom-in { animation: zoomIn 1.5s cubic-bezier(0.22,1,0.36,1) forwards; }
    
    .toggle-on-hover { transition: filter 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important; }
    .toggle-on-hover:hover { filter: brightness(0.85) !important; transform: translateY(-5px) !important; animation: none !important; }
    
    .black-border-card { border: 1px solid black !important; }
    .black-border-card:hover { border: 1px solid black !important; }

    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.001ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.001ms !important;
      }
    }

    /* Responsive Grid Utilities */
    .grid-cols-4 { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
    .grid-cols-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .grid-cols-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
    .grid-cols-1 { display: grid; grid-template-columns: 1fr; gap: 16px; }
    .grid-cols-5 { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; }

    /* Flex Utilities */
    .flex-row { display: flex; flex-direction: row; }
    .flex-col { display: flex; flex-direction: column; }
    .flex-wrap { flex-wrap: wrap; }

    /* Responsive overrides */
    .hide-desktop { display: none !important; }

    .login-box-padding { padding: 48px; }
    .login-logo-outer { width: 110px; height: 110px; margin-bottom: 20px; }
    .login-logo-inner { width: 90px; height: 90px; }
    .login-title { font-size: 40px; margin-bottom: 6px; }
    .login-subtitle { font-size: 20px; }
    .login-form-title { font-size: 22px; margin-bottom: 32px; }
    .login-input-gap { gap: 20px; }

    @media (max-width: 1024px) {
      .grid-cols-4, .grid-cols-5 { grid-template-columns: repeat(2, 1fr) !important; }
      .grid-cols-3 { grid-template-columns: repeat(2, 1fr) !important; }
      .hide-tablet { display: none !important; }
    }

    @media (max-width: 768px) {
      .grid-cols-4, .grid-cols-3, .grid-cols-2, .grid-cols-5 { grid-template-columns: 1fr !important; }
      .hide-mobile { display: none !important; }
      .hide-desktop { display: inline-flex !important; }
      .flex-mobile-col { flex-direction: column !important; align-items: flex-start !important; }
      .mobile-w-full { width: 100% !important; }
      
      .mobile-p-10 { padding: 10px !important; }
      .mobile-p-16 { padding: 16px !important; }
      .mobile-p-20 { padding: 20px !important; }
      .mobile-mb-16 { margin-bottom: 16px !important; }
      .login-box-padding { padding: 24px !important; }
      .login-logo-outer { width: 130px !important; height: 130px !important; margin-bottom: 20px !important; }
      .login-logo-inner { width: 110px !important; height: 110px !important; }
      .login-title { font-size: 24px !important; line-height: 1.1 !important; margin-bottom: 2px !important; }
      .login-subtitle { font-size: 13px !important; }
      .login-form-title { font-size: 18px !important; margin-bottom: 16px !important; }
      .login-input-gap { gap: 14px !important; }
      .topbar-container { padding: 0 12px !important; gap: 8px !important; }
      .topbar-title-container { position: static !important; transform: none !important; left: auto !important; flex: 1; min-width: 0; justify-content: flex-start !important; }
      .topbar-title-text { font-size: 14px !important; text-overflow: ellipsis; overflow: hidden; white-space: nowrap; display: block; }
      
      .table-responsive-wrapper { overflow-x: auto; width: 100%; -webkit-overflow-scrolling: touch; }
      
      /* Sidebar overlays */
      .sidebar-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1040; display: block; animation: fadeIn 0.2s; }
      .sidebar-mobile { position: fixed !important; top: 0; bottom: 0; left: 0; z-index: 1050; transform: translateX(-100%); transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1); }
      .sidebar-mobile.open { transform: translateX(0); }
    }

    /* Keep the date picker calendar above sticky table headers */
    .react-datepicker-popper { z-index: 999999 !important; }
  `}</style>
    {/* dark-mode-only fixes for colours set inline on older screens (src/darkTheme.js) */}
    <style>{DARK_THEME_CSS}</style>
  </>
);
